import React from 'react';
import useAdvancedParallax from '../hooks/useAdvancedParallax';
import './ParallaxLayer.css';

/**
 * ParallaxLayer component for creating sophisticated parallax effects
 * @param {object} props - Component props
 * @param {string} props.type - Layer type ('base', 'shapes', 'patterns', 'details', 'particles', 'interactive')
 * @param {number} props.scrollSpeed - Scroll parallax speed (0.1-1.0)
 * @param {number} props.mouseSpeed - Mouse tracking speed (0.1-1.0)
 * @param {boolean} props.enableMouse - Enable mouse tracking
 * @param {boolean} props.enableScroll - Enable scroll parallax
 * @param {string} props.axis - Parallax axis ('x', 'y', 'both')
 * @param {number} props.mouseInfluence - Mouse influence strength (0-1)
 * @param {string} props.className - Additional CSS classes
 * @param {object} props.style - Inline styles
 * @param {React.ReactNode} props.children - Child elements
 * @returns {JSX.Element} ParallaxLayer component
 */
const ParallaxLayer = ({
  type = 'base',
  scrollSpeed = 0.5,
  mouseSpeed = 0.1,
  enableMouse = true,
  enableScroll = true,
  axis = 'y',
  mouseInfluence = 0.3,
  className = '',
  style = {},
  children,
  ...props
}) => {
  // Configure parallax based on layer type
  const getParallaxConfig = () => {
    const configs = {
      base: {
        scrollSpeed: 0.2,
        mouseSpeed: 0.05,
        mouseInfluence: 0.1,
        axis: 'y'
      },
      shapes: {
        scrollSpeed: 0.4,
        mouseSpeed: 0.1,
        mouseInfluence: 0.2,
        axis: 'both'
      },
      patterns: {
        scrollSpeed: 0.6,
        mouseSpeed: 0.15,
        mouseInfluence: 0.3,
        axis: 'both'
      },
      details: {
        scrollSpeed: 0.8,
        mouseSpeed: 0.2,
        mouseInfluence: 0.4,
        axis: 'both'
      },
      particles: {
        scrollSpeed: 1.0,
        mouseSpeed: 0.3,
        mouseInfluence: 0.6,
        axis: 'both'
      },
      interactive: {
        scrollSpeed: 1.2,
        mouseSpeed: 0.4,
        mouseInfluence: 0.8,
        axis: 'both'
      }
    };

    return {
      ...configs[type],
      scrollSpeed: scrollSpeed || configs[type].scrollSpeed,
      mouseSpeed: mouseSpeed || configs[type].mouseSpeed,
      mouseInfluence: mouseInfluence || configs[type].mouseInfluence,
      axis: axis || configs[type].axis
    };
  };

  const config = getParallaxConfig();
  const { ref, isVisible, hasIntersected } = useAdvancedParallax({
    scrollSpeed: config.scrollSpeed,
    mouseSpeed: config.mouseSpeed,
    enableMouse: enableMouse && type !== 'base', // Base layer doesn't need mouse tracking
    enableScroll: enableScroll,
    axis: config.axis,
    mouseInfluence: config.mouseInfluence,
    intersectionOptions: {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }
  });

  const layerClassName = `parallax-layer parallax-${type} ${className}`.trim();

  return (
    <div
      ref={ref}
      className={layerClassName}
      style={style}
      data-layer-type={type}
      data-is-visible={isVisible}
      data-has-intersected={hasIntersected}
      {...props}
    >
      {children}
    </div>
  );
};

export default ParallaxLayer;
